import { create } from "zustand";
import api, { apiFormData } from "../../utils/api";
import type { Pagination } from "./hikvisionAccessStore";
import {
  providerVisitComplete,
  specialInspectionKind,
} from "./specialInspection";
import {
  REQUIRED_PHOTO_TITLES,
  inspectorDataMissing,
  manualExitMissing,
} from "./inspectionCompleteness";

// ─── Types ────────────────────────────────────────────────────────────────────

export type InspectionStatus =
  | "complete"
  | "cancelled"
  | "pending_exit"
  | "missing_exit"
  | "missing_vehicle"
  | "incomplete"
  // Visita con el tag de proveedores del patio: no hay descarga que
  // inspeccionar, pero SÍ tres datos que capturar (quién vino, quién lo
  // recibió, a qué). Pendiente mientras falte alguno.
  | "provider_pending"
  | "provider_complete";

/**
 * How long a visit may stay open before we stop calling the truck "en patio"
 * and start calling its exit lost.
 *
 * "Still inside" and "we never saw it leave" are opposite claims, and until now
 * both rendered as "En patio" — which is how a visit from 10/06 sat in the
 * grid for 83 days looking exactly like a truck that arrived this morning.
 *
 * Eight hours, and the number is free rather than arbitrary. Of the 74 closed
 * visits in production, 11 ran longer than 8 h and 11 ran longer than 12 h —
 * the same eleven. Not one closed visit lands between the two, so moving the
 * flag from 12 h down to 8 h marks exactly the same set and buys four hours of
 * warning for nothing. Dev agrees: 10 of 81 either way.
 *
 * The real scale: the median closed visit lasts 0.36 h — twenty-two minutes, a
 * discharge and out — and the raw controller log puts real passes at 13-60 min.
 * Eight hours is ~22x the median and ~8x the longest real pass.
 *
 * Do NOT re-tune this against the p90 of 23.9 h. Every one of the seven closed
 * visits over 24 h in production was inspected individually and every one is a
 * mis-pairing: a tanker returning every ~3 days, each exit closing the PREVIOUS
 * trip's visit (74.6 / 73.3 / 71.7 / 68.5 h are all the same ~3-day artefact).
 * That population was manufactured by the very defect this label exposes, so
 * fitting to it would be circular. The p50 is real; the p90 is not.
 *
 * ── Why this is 8 h while the backend's pairing window is 24 h ─────────────
 * They answer different questions, and the flag SHOULD be tighter than the cap.
 * HIKVISION_INSPECTION_PAIRING_WINDOW_HOURS (24 h) is a data decision: too
 * short and it rejects a legitimate exit and stamps `exit_missing_at` on a
 * visit that actually ended fine. That error is destructive, so it errs
 * generous. This constant only paints a label: too short and it says "this
 * looks stale" a few hours early, then switches itself off the moment the exit
 * lands. That error is noise, so it errs early. A flag that waits 24 h is also
 * useless to the operator — the shift is over and nobody walked the yard.
 *
 * The two never contradict: `exit_missing_at`, the backend's observation, wins
 * outright over this heuristic (see hasLostExit). Its mark catches "the vehicle
 * came back"; this threshold catches "it never came back at all" — the gap the
 * backend cannot reach, and no corner case here: 11 of the 20 tags at Acuapue
 * have never once been read by the exit lane.
 *
 * Applied to the ten visits open in production today it flags three (folios 77,
 * 86 and 100 — open 120 h, 106 h and 34 h) and leaves this afternoon's arrivals
 * alone.
 *
 * This is a display threshold — nothing is written and no exit is invented.
 */
export const MISSING_EXIT_AFTER_HOURS = 8;

/**
 * Millis from the shapes `entry_timestamp` actually arrives in. The field is
 * typed `string | null` but /yardInspectionList converts it to epoch millis
 * before it ever reaches us, and a Firestore Timestamp can still show up on
 * paths that skip that endpoint. Anything unrecognised yields 0, which reads as
 * "no usable entry time" and keeps the visit out of the stale bucket.
 */
function inspectionMillis(ts: unknown): number {
  if (!ts) return 0;
  if (typeof ts === "number") return ts;
  if (typeof ts === "string") {
    const d = new Date(ts).getTime();
    return Number.isNaN(d) ? 0 : d;
  }
  if (typeof ts === "object") {
    const o = ts as Record<string, unknown>;
    if (typeof o._seconds === "number") return o._seconds * 1000;
    if (typeof o.seconds === "number") return o.seconds * 1000;
  }
  return 0;
}

/**
 * La fecha que "es" una visita: la de la ENTRADA, con la salida de respaldo y
 * el ingest como último recurso. Espejo exacto de `inspectionAnchorMs` del
 * backend (hikvisionAccess/inspectionDayAnchor.js), que es lo que decide qué
 * inspecciones caen en el rango pedido — si el cliente ordenara o imprimiera
 * otra fecha, la lista contradiría al filtro que la trajo.
 *
 * `createdAt` es el serverTimestamp del INGEST, no la hora del camión: el sync
 * del controlador crea hoy inspecciones cuya visita fue hace semanas. Ordenar
 * o rotular por él ponía la visita de junio arriba de la de esta mañana.
 *
 * Devuelve 0 cuando no hay ningún timestamp usable, que es lo que
 * `formatYardTs` lee como "—".
 */
export function inspectionDateMillis(inspection: YardInspection): number {
  return (
    inspectionMillis(inspection.entry_timestamp) ||
    inspectionMillis(inspection.exit_timestamp) ||
    inspectionMillis(inspection.createdAt)
  );
}

// Las fotos obligatorias y "qué le falta a una inspección" viven en un módulo
// puro (inspectionCompleteness.ts) con sus tests; se reexportan para que los
// que ya importan de este store no cambien.
export { REQUIRED_PHOTO_TITLES, inspectorDataMissing, manualExitMissing };

/**
 * Los tres parámetros del agua, ahora también como NÚMERO y no sólo como el
 * título de una foto.
 *
 * Hasta este cambio pH, conductividad y turbidez existían únicamente como
 * `REQUIRED_PHOTO_TITLES`: tres slots de fotografía. El valor quedaba en el
 * píxel de la foto del medidor, así que no había nada que promediar — un grep
 * de `turbidez|conductividad|ph_level` sobre `functions/src` daba cero. La foto
 * sigue siendo la evidencia; el campo es el dato.
 *
 * `field` es el nombre del campo en el documento `yardInspections` y en el
 * cuerpo del `PUT /yardInspection` — es contrato con el backend, no un detalle
 * de UI. `photoTitle` lo ata al slot de foto correspondiente para que el
 * inspector lea el medidor una sola vez.
 *
 * Los valores son `string` libre, igual que `volumen_extraido`, para que los
 * decimales sobrevivan intactos tal como se tecleó (0.5 no se vuelve 0,5 ni
 * 0.49999).
 *
 * ── Unidades ──────────────────────────────────────────────────────────────
 * CONFIRMADAS CON OPERACIÓN el 6-sep-2026: pH adimensional, conductividad en
 * µS/cm, turbidez en NTU. Eran las unidades habituales de un medidor de campo y
 * nadie las había verificado contra los equipos de Acuapue (#502); se preguntó
 * mientras el histórico seguía vacío, que era la única ventana en la que
 * corregirlo salía gratis. `unit` se sigue PINTANDO junto al input, ahora como
 * control de un patio NUEVO: si algún día llega un medidor en mS/cm, el
 * inspector lo ve y avisa antes de que se mezclen dos escalas en la misma
 * columna — un promedio sobre µS/cm y mS/cm revueltos no significa nada y no
 * hay forma de separarlos después.
 *
 * ── Rangos (`min`/`max`) ──────────────────────────────────────────────────
 * Son para AVISAR, nunca para bloquear: un pH de 47 es casi siempre un dedazo,
 * pero rechazar el guardado deja la inspección sin cerrar, que es peor que un
 * número raro. Están puestos donde el valor deja de ser interpretable, no donde
 * deja de ser común, porque no hay ni una sola medición histórica con la cual
 * calibrar algo más fino (el histórico es fotos):
 *   - pH 0-14 es la definición de la escala, no una estimación.
 *   - 200 000 µS/cm es salmuera saturada; el agua de mar ronda 50 000. Por
 *     encima de eso el número no es una lectura en µS/cm.
 *   - 10 000 NTU está por encima del fondo de escala de cualquier turbidímetro
 *     de campo (los portátiles topan en 1 000-4 000 NTU).
 * Cuando haya meses de capturas reales, estos techos se pueden apretar contra
 * la distribución medida; hasta entonces apretarlos sería inventar.
 */
export const WATER_PARAMETERS = [
  {
    key: "ph",
    field: "ph",
    photoTitle: REQUIRED_PHOTO_TITLES[0],
    label: "pH",
    unit: "",
    min: 0,
    max: 14,
    placeholder: "Ej. 7.2",
  },
  {
    key: "conductividad",
    field: "conductividad",
    photoTitle: REQUIRED_PHOTO_TITLES[1],
    label: "Conductividad",
    unit: "µS/cm",
    min: 0,
    max: 200_000,
    placeholder: "Ej. 1250",
  },
  {
    key: "turbidez",
    field: "turbidez",
    photoTitle: REQUIRED_PHOTO_TITLES[2],
    label: "Turbidez",
    unit: "NTU",
    min: 0,
    max: 10_000,
    placeholder: "Ej. 35",
  },
] as const;

export type WaterParameterKey = (typeof WATER_PARAMETERS)[number]["key"];

/**
 * El ÚNICO parser de una lectura del medidor. Devuelve el número, o `null` si
 * el texto no es uno.
 *
 * Existe como función exportada porque el validador de la UI y el consumidor
 * del dato (los promedios de #500/#501) tienen que leer el string EXACTAMENTE
 * igual. Cuando no lo hacían, `"1,250"` — mil doscientos cincuenta escrito
 * como se escribe en es-MX, y un valor de conductividad de todos los días —
 * pasaba el aviso de rango como 1.25 µS/cm mientras el string que quedaba
 * guardado en Firestore seguía siendo `"1,250"`, que `Number()` lee como NaN.
 * El validador avalaba un valor que el consumidor no podía parsear.
 *
 * Reglas, en orden:
 *  - La coma es separador DECIMAL, no de miles: "7,2" es 7.2. Es lo que se
 *    teclea en es-MX y el motivo de que el campo sea string.
 *  - Más de un separador (`1,250,000`, `1.250,75`) no es una lectura: es un
 *    número agrupado, y no hay forma de saber cuál es el decimal.
 *  - Una sola coma seguida de exactamente tres dígitos (`1,250`) es ambigua
 *    entre 1.25 y 1250, y las dos lecturas difieren por tres órdenes de
 *    magnitud. Se rechaza en vez de elegir una: el aviso le pide al inspector
 *    que la escriba sin agrupar, que es lo único que la desambigua.
 */
export function parseWaterParameterValue(
  raw: string | null | undefined,
): number | null {
  const text = String(raw ?? "").trim();
  if (!text) return null;
  const separators = text.match(/[.,]/g)?.length ?? 0;
  if (separators > 1) return null;
  if (/^[+-]?\d{1,3},\d{3}$/.test(text)) return null;
  const value = Number(text.replace(/,/g, "."));
  return Number.isFinite(value) ? value : null;
}

/**
 * Aviso de rango, NUNCA bloqueo. Devuelve `null` cuando el valor está vacío
 * (sigue siendo opcional: ver `hasInspectorData`) o es plausible; en caso
 * contrario, el texto que se pinta debajo del input.
 *
 * El vacío no avisa a propósito: el inspector que todavía no llegó al medidor
 * no tiene que ver una alerta por no haber escrito nada.
 */
export function waterParameterWarning(
  key: WaterParameterKey,
  raw: string | null | undefined,
): string | null {
  const spec = WATER_PARAMETERS.find((p) => p.key === key);
  if (!spec) return null;
  const text = String(raw ?? "").trim();
  if (!text) return null;
  const value = parseWaterParameterValue(text);
  if (value === null) {
    // El texto agrupado se distingue del garabato porque tiene arreglo obvio y
    // el inspector es quien puede darlo: "1,250" son 1250 o 1.25 y sólo él
    // sabe cuál leyó en el medidor. "siete" no tiene ese arreglo, y decirle
    // "quitá el separador de miles" sería un consejo inútil.
    const withoutSeparators = text.replace(/[.,]/g, "");
    if (
      /[.,]/.test(text) &&
      /\d/.test(withoutSeparators) &&
      Number.isFinite(Number(withoutSeparators))
    ) {
      return "No parece un número: escribilo sin separador de miles (1250, no 1,250).";
    }
    return "No parece un número.";
  }
  const unit = spec.unit ? ` ${spec.unit}` : "";
  if (value < spec.min || value > spec.max) {
    return `Fuera del rango esperado (${spec.min}-${spec.max}${unit}). Se guarda igual, verificá la lectura.`;
  }
  return null;
}

export interface YardInspectionEvidence {
  id: string;
  imageUrl: string;
  fileName?: string | null;
  title?: string;
  description?: string;
  order?: number;
  timestamp?: number | null;
  createdAt?: number | null;
  updatedAt?: number | null;
}

export interface YardInspection {
  id: string;
  company_id: string;
  // Consecutive per-company folio assigned at creation. Null for legacy
  // inspections created before folios existed.
  folio?: number | null;
  // "provider" marks a supplier visit — no discharge, so the inspection is
  // born cancelled. Stamped at creation by the backend from the vehicle's
  // `is_provider` flag; null/absent for an ordinary discharge inspection.
  //
  // "untagged" es la pipa que entró con el tag compartido de "pipas sin tag":
  // una descarga normal a la que primero hay que decirle qué unidad es.
  inspection_type?: string | null;
  // Presente SÓLO cuando la inspección nació de un tag especial del patio
  // (`truck_yards/{id}.special_tags`). Es lo que separa al proveedor nuevo
  // —modal de tres campos, estados provider_*— del legacy de `is_provider`,
  // que también lleva `inspection_type: "provider"` y sigue naciendo
  // cancelado. Ver specialInspectionKind.
  special_tag?: "provider" | "untagged" | null;
  // Proveedor: los tres datos de la visita. Sólo los acepta el backend cuando
  // `inspection_type === "provider"`; en otro tipo los ignora.
  provider_name?: string | null;
  received_by?: string | null;
  visit_reason?: string | null;
  // Pipa sin tag: sello del paso 1 (`PUT /yardInspection/registration`).
  // Timestamp de Firestore, así que puede llegar como `{_seconds}`, epoch o
  // ISO según el endpoint: sólo se lee como "está / no está".
  registration_completed_at?: unknown;
  registered_by_id?: string | null;
  registered_by_name?: string | null;
  // Pipa sin tag, paso 1: "¿trae tag?" ("unreadable" | "none") y, si lo trae,
  // dónde va en el parabrisas. Ver TAG_*_OPTIONS en specialInspection.ts.
  tag_presence?: string | null;
  tag_position?: string | null;
  yard_id: string;
  yard_name: string;
  access_entry_id: string | null;
  access_exit_id: string | null;
  entry_timestamp: string | null;
  exit_timestamp: string | null;
  vehicle_id: string | null;
  vehicle_plate: string | null;
  // Card/tag identity carried from the access event — shown as a fallback when
  // the card isn't mapped to a vehicle (mirrors the access screen).
  card_number?: string | null;
  driver_id: string | null;
  driver_name: string | null;
  inspector_id: string | null;
  inspector_name: string | null;
  title?: string | null;
  description?: string | null;
  notes: string | null;
  // Water type observed during this inspection — one of the three catalog
  // values (agua_residual / agua_en_proceso / agua_biosolidos). Chosen by the
  // operator in the "Ver / Completar" modal and compared against the vehicle's
  // assigned types in the PDF report.
  water_type?: string | null;
  // Water type the transportista DECLARED (agua reportada), as opposed to
  // `water_type` above which is what was actually extracted/inspected (agua
  // extraída). Same catalog values.
  water_type_reported?: string | null;
  // Extracted volume in LITRES (free-form string so decimals survive). The
  // printed format's label reads "M³/L" because it admits both; the platform
  // captures litres, and the PDF labels the value accordingly.
  volumen_extraido?: string | null;
  // Parámetros del agua leídos del medidor, junto a la foto que los prueba.
  // String libre por la misma razón que `volumen_extraido`: los decimales.
  // Ausentes en toda inspección anterior a #502 — cualquier consumidor
  // (promedios incluidos) tiene que saltárselos, no contarlos como 0.
  // Unidades: ver WATER_PARAMETERS.
  ph?: string | null;
  conductividad?: string | null;
  turbidez?: string | null;
  // Stamped by the backend when a later pass of the SAME identifier proves this
  // visit outlived HIKVISION_INSPECTION_PAIRING_WINDOW_HOURS without closing.
  // Observed evidence that the exit was never recorded — outranks the
  // age-based heuristic in hasLostExit.
  exit_missing_at?: unknown;
  exit_missing_reason?: string | null;
  evidences?: YardInspectionEvidence[];
  geofence_event_id?: string | null;
  // Los dos cruces de geocerca que el backend vinculó a ESTA visita
  // (geofenceCrossingResolver): epoch en milisegundos. Viajan desde siempre en
  // `/yardInspectionList`, que devuelve el documento entero; sólo faltaba
  // declararlos. Ausentes en toda inspección sin cruce vinculado —vehículo sin
  // GPS, patio sin geocerca, o salida que todavía no ocurrió—, y `null` cuando
  // el resolver soltó una pata que dejó de cumplir las reglas (#721). Ninguno
  // de los dos casos es 0: se leen como "Sin cruce" (inspectionTimes.ts).
  geofence_entry_at?: number | null;
  geofence_exit_at?: number | null;
  createdAt: string;
  updatedAt: string;
}

// Whether the inspector actually captured the inspection: the water type they
// observed, the volume they measured, and the three required photographs.
// Everything else on the visit (vehicle, entry, exit) is written by the access
// control hardware — this is the part a human has to do.
//
// `ph`, `conductividad` y `turbidez` NO se exigen aquí, y la omisión es
// deliberada (#502). Los campos nacieron vacíos: ninguna inspección anterior a
// este cambio los tiene, así que sumarlos al criterio volvería `incomplete` de
// golpe a TODAS las históricas que hoy están completas, y los conteos por
// estado mentirían desde el primer día. Endurecer el criterio es una decisión
// posterior y separada, y necesita una fecha de corte: exigir el número sólo a
// las inspecciones creadas después de que el campo existiera.
export function hasInspectorData(inspection: YardInspection): boolean {
  // La misma lista que decide si se puede dar salida manual: si cambia el
  // criterio de "Completa", cambia en los dos lados a la vez.
  return inspectorDataMissing(inspection).length === 0;
}

/**
 * A visit still open past MISSING_EXIT_AFTER_HOURS. Not a claim that the truck
 * is gone — a claim that the platform never saw it leave, which is a different
 * and far more common thing: 11 of the 20 tags at Acuapue have never once been
 * read by the exit lane.
 */
export function hasLostExit(
  inspection: YardInspection,
  now: number = Date.now(),
): boolean {
  if (inspection.access_exit_id) return false;

  // Observation beats heuristic. `exit_missing_at` is stamped by the backend
  // when the SAME tag passes again while this visit is still open — proof the
  // visit never closed, not an inference from elapsed time. When it is there,
  // no clock arithmetic is needed or wanted.
  if (inspection.exit_missing_at) return true;

  // Otherwise fall back to age. This is the case the backend's mark cannot
  // reach: the vehicle that simply never comes back, so nothing ever re-triggers
  // the pairing check.
  const enteredAt = inspectionMillis(inspection.entry_timestamp);
  if (!enteredAt) return false;
  return now - enteredAt > MISSING_EXIT_AFTER_HOURS * 3_600_000;
}

/**
 * `now` is injectable so the status stays deterministic under test — this is
 * the one status that depends on the clock rather than on the document alone.
 */
export function deriveInspectionStatus(
  inspection: YardInspection,
  now: number = Date.now(),
): InspectionStatus {
  // El tag de proveedores del patio va ANTES que el proveedor legacy, y el
  // orden importa: los dos llevan `inspection_type: "provider"`, y lo único que
  // los separa es `special_tag`. Con el orden invertido toda visita del tag
  // nuevo saldría "Cancelada" y nadie capturaría sus tres datos.
  if (specialInspectionKind(inspection) === "provider") {
    return providerVisitComplete(inspection)
      ? "provider_complete"
      : "provider_pending";
  }

  // A supplier ("proveedor") drives through the yard without discharging, so
  // there is nothing to inspect and the visit is cancelled on arrival. Checked
  // first: it outranks every other state, including a missing exit.
  if (inspection.inspection_type === "provider") return "cancelled";

  // La pipa sin tag no tiene rama propia a propósito: es una descarga normal.
  // Antes del registro manual no tiene unidad, así que cae sola en
  // `missing_vehicle`, que es exactamente lo que le falta.

  const hasVehicle = !!inspection.vehicle_id || !!inspection.vehicle_plate;
  const hasExit = !!inspection.access_exit_id;

  // Checked before the exit: without a unit we cannot even say WHICH truck we
  // lost, and identifying it is the more fundamental repair.
  if (!hasVehicle) return "missing_vehicle";
  if (!hasExit) {
    return hasLostExit(inspection, now) ? "missing_exit" : "pending_exit";
  }
  // Deliberately NOT gated on a driver. The Acuapue tags map card → VEHICLE and
  // carry no person, so `driver_name` is null on every event the hardware
  // produces; requiring it here kept fully-captured inspections (folio 112:
  // water type, 83.2 L, all three photos) permanently stuck on "Incompleta".
  return hasInspectorData(inspection) ? "complete" : "incomplete";
}

export function inspectionStatusLabel(status: InspectionStatus): string {
  switch (status) {
    case "complete":
      return "Completa";
    case "cancelled":
      return "Cancelada";
    case "pending_exit":
      return "En patio";
    // NOT "En patio": the point of this state is that we do not know where the
    // unit is. The wording has to say the record is missing, not the truck.
    case "missing_exit":
      return "Sin salida";
    case "missing_vehicle":
      return "Sin vehículo";
    case "incomplete":
      return "Incompleta";
    case "provider_pending":
      return "Proveedor pendiente";
    case "provider_complete":
      return "Proveedor registrado";
  }
}

/**
 * The one place a status turns into colour.
 *
 * Three surfaces need the same hue for the same state — the status pill, the
 * card/row that carries it, and the focus ring of the active filter pill — and
 * until now each kept its own copy of the map (StatusPill, YardInspectionCard
 * and YardInspectionListRow in inspectionHistorySection.tsx all repeated it).
 * Three copies is how `missing_exit` shipped in the pill while the card behind
 * it still had five states and fell through to the neutral border.
 *
 * Every class is written out in full on purpose: Tailwind scans source text,
 * so a hue assembled at runtime (`border-${hue}-500/40`) yields no CSS at all.
 *
 * Hues: green complete, gray cancelled, blue "en patio", orange "sin salida",
 * red "sin vehículo", yellow "incompleta", purple "proveedor pendiente", teal
 * "proveedor registrado".
 */
const INSPECTION_STATUS_PALETTE: Record<
  InspectionStatus,
  { pill: string; accent: string; ring: string }
> = {
  complete: {
    pill: "bg-green-500/15 text-green-400 border border-green-500/30",
    accent: "border-green-500/40 ring-1 ring-green-500/20",
    ring: "ring-green-500/70",
  },
  cancelled: {
    pill: "bg-gray-500/15 text-gray-400 border border-gray-500/30",
    accent: "border-gray-500/40 ring-1 ring-gray-500/20",
    ring: "ring-gray-500/70",
  },
  pending_exit: {
    pill: "bg-blue-500/15 text-blue-400 border border-blue-500/30",
    accent: "border-blue-500/40 ring-1 ring-blue-500/20",
    ring: "ring-blue-500/70",
  },
  // Orange: a problem, but not the same problem as a missing vehicle (red)
  // nor an unfinished capture (yellow). Deliberately not blue — reading as a
  // calm sibling of "En patio" is exactly the confusion being removed.
  //
  // The accent goes further than a hue swap: /70 border and ring-2 against
  // pending_exit's /40 and ring-1. At card size a 40%-opacity border is barely
  // a tint, and orange-at-40% next to blue-at-40% is precisely the "two calm
  // siblings" read this state exists to break. This one is meant to be the
  // loudest card on the grid after `missing_vehicle`.
  missing_exit: {
    pill: "bg-orange-500/15 text-orange-400 border border-orange-500/30",
    accent: "border-orange-500/70 ring-2 ring-orange-500/25",
    ring: "ring-orange-500/70",
  },
  missing_vehicle: {
    pill: "bg-red-500/15 text-red-400 border border-red-500/30",
    accent: "border-red-500/40 ring-1 ring-red-500/20",
    ring: "ring-red-500/70",
  },
  incomplete: {
    pill: "bg-yellow-500/15 text-yellow-400 border border-yellow-500/30",
    accent: "border-yellow-500/40 ring-1 ring-yellow-500/20",
    ring: "ring-yellow-500/70",
  },
  // Ni amarillo ni verde, aunque "pendiente / hecho" los sugiera: esos dos ya
  // significan "inspección de descarga a medias / terminada", y una visita de
  // proveedor no es una descarga. Con el mismo hue, la píldora "Incompleta 4"
  // sumaría a la vista proveedores que no tienen ni fotos ni volumen que
  // completar. Morado y verde azulado no los usa ningún otro estado.
  provider_pending: {
    pill: "bg-purple-500/15 text-purple-400 border border-purple-500/30",
    accent: "border-purple-500/40 ring-1 ring-purple-500/20",
    ring: "ring-purple-500/70",
  },
  provider_complete: {
    pill: "bg-teal-500/15 text-teal-400 border border-teal-500/30",
    accent: "border-teal-500/40 ring-1 ring-teal-500/20",
    ring: "ring-teal-500/70",
  },
};

/** Status pill: tinted background, coloured text, matching hairline border. */
export function inspectionStatusClasses(status: InspectionStatus): string {
  return INSPECTION_STATUS_PALETTE[status].pill;
}

/**
 * Border + halo for the card or row carrying this status, so the grid reads as
 * colour before it reads as text — the same trick the access-event cards
 * already use. Replaces `themeClasses.cardBorder`, so it has to supply the
 * whole border in both themes.
 */
export function inspectionStatusAccentClasses(status: InspectionStatus): string {
  return INSPECTION_STATUS_PALETTE[status].accent;
}

/** Focus ring drawn around the filter pill that is currently active. */
export function inspectionStatusRingClasses(status: InspectionStatus): string {
  return INSPECTION_STATUS_PALETTE[status].ring;
}

export function inspectionStatusIcon(status: InspectionStatus): string {
  switch (status) {
    case "complete":
      return "ri-checkbox-circle-fill";
    case "cancelled":
      return "ri-close-circle-line";
    case "pending_exit":
      return "ri-time-line";
    case "missing_exit":
      return "ri-question-line";
    case "missing_vehicle":
      return "ri-truck-line";
    case "incomplete":
      return "ri-error-warning-line";
    case "provider_pending":
      return "ri-user-received-2-line";
    case "provider_complete":
      return "ri-user-follow-line";
  }
}

// ─── Store ────────────────────────────────────────────────────────────────────

interface YardInspectionsStoreState {
  inspections: YardInspection[];
  inspectionsLoading: boolean;
  setInspections: (inspections: YardInspection[]) => void;
  setInspectionsLoading: (loading: boolean) => void;
  updateInspectionInList: (inspection: YardInspection) => void;
}

export const useYardInspectionsStore = create<YardInspectionsStoreState>(
  (set) => ({
    inspections: [],
    inspectionsLoading: false,
    setInspections: (inspections) => set({ inspections }),
    setInspectionsLoading: (loading) =>
      set({ inspectionsLoading: loading }),
    updateInspectionInList: (inspection) =>
      set((s) => ({
        inspections: s.inspections.map((i) =>
          i.id === inspection.id ? inspection : i,
        ),
      })),
  }),
);

// ─── API Functions ────────────────────────────────────────────────────────────

export interface FetchYardInspectionsParams {
  yard_id?: string;
  /**
   * Varios patios separados por coma (filtro múltiple de Accesos y del
   * Historial de Inspecciones). El backend lo ignora si también viene
   * `yard_id`, que manda.
   */
  yard_ids?: string;
  limit?: number;
  page?: number;
  date_from?: string | number;
  date_to?: string | number;
  /**
   * Suma al rango toda visita que siga abierta (sin `exit_timestamp`), sin
   * importar cuándo entró. Sin esto, el default "hoy" del historial borra de la
   * pantalla al camión que entró hace tres días y no registró salida, y deja en
   * cero las píldoras "Sin salida" y "En patio", que son las que existen para
   * señalarlo. Ver inspectionDayAnchor.js en el backend.
   */
  include_open?: boolean;
}

export const fetchYardInspections = async (
  params: FetchYardInspectionsParams = {},
): Promise<{ data: YardInspection[]; pagination: Pagination }> => {
  const res = await api.get("/yardInspectionList", { params });
  return res.data;
};

export interface UpdateYardInspectionParams {
  id: string;
  access_exit_id?: string | null;
  exit_timestamp?: string | null;
  vehicle_id?: string | null;
  vehicle_plate?: string | null;
  driver_id?: string | null;
  driver_name?: string | null;
  inspector_id?: string | null;
  inspector_name?: string | null;
  title?: string | null;
  description?: string | null;
  notes?: string | null;
  water_type?: string | null;
  water_type_reported?: string | null;
  volumen_extraido?: string | null;
  ph?: string | null;
  conductividad?: string | null;
  turbidez?: string | null;
  // Sólo para la visita de proveedor; el backend los ignora en otro tipo.
  provider_name?: string | null;
  received_by?: string | null;
  visit_reason?: string | null;
}

export const updateYardInspection = async (
  data: UpdateYardInspectionParams,
): Promise<YardInspection> => {
  const res = await api.put("/yardInspection", data);
  return res.data.data;
};

/**
 * "DAR SALIDA MANUAL": el backend crea el evento de salida de ESTA visita y la
 * cierra con él (POST /yardInspection/manualExit). Devuelve la inspección ya
 * cerrada. 409 si ya tenía salida — un doble clic no crea dos.
 * La pluma la levanta quien llama (openYardGate), no el backend.
 */
export const createManualExitForInspection = async (
  id: string,
  // Por qué no la leyó la lectora de salida (exitTagSurveyPayload); el
  // backend lo guarda en exit_tag_presence / exit_tag_position.
  survey: { tag_presence?: string | null; tag_position?: string | null },
): Promise<YardInspection> => {
  const res = await api.post("/yardInspection/manualExit", { id, ...survey });
  return res.data.data;
};

/**
 * Paso 1 de la pipa sin tag: qué unidad entró con el tag compartido.
 *
 * Una clave AUSENTE no es lo mismo que una en `null`: `notes` sólo se manda
 * cuando el formulario la conoce (ver registrationPayload), para que corregir
 * la placa no borre la nota que ya tenía el evento de entrada.
 */
export interface UpdateYardInspectionRegistrationParams {
  id: string;
  vehicle_id?: string | null;
  vehicle_plate?: string | null;
  driver_name?: string | null;
  notes?: string | null;
  tag_presence?: string | null;
  tag_position?: string | null;
}

/**
 * Además de la inspección, el backend actualiza en el mismo batch el evento de
 * ENTRADA (y el de salida si ya existe) con la unidad capturada. Es
 * idempotente: se vuelve a mandar para corregir.
 *
 * El id viaja en la query —así lo define el contrato del endpoint— Y en el
 * body: `PUT /yardInspection`, el vecino, lo lee de `req.body.id`
 * (hikvisionAccessController.putYardInspection), y repetirlo cuesta nada
 * frente a un 404 por haber adivinado mal de dónde lo saca el handler nuevo.
 */
export const updateYardInspectionRegistration = async (
  data: UpdateYardInspectionRegistrationParams,
): Promise<YardInspection> => {
  const res = await api.put("/yardInspection/registration", data, {
    params: { id: data.id },
  });
  return res.data.data;
};

export interface UploadYardInspectionEvidenceParams {
  inspectionId: string;
  file: File;
  title?: string;
  description?: string;
}

export const uploadYardInspectionEvidence = async (
  params: UploadYardInspectionEvidenceParams,
): Promise<YardInspectionEvidence> => {
  const form = new FormData();
  form.append("inspection_id", params.inspectionId);
  form.append("evidence", params.file);
  if (params.title) form.append("title", params.title);
  if (params.description) form.append("description", params.description);
  form.append("timestamp", String(Date.now()));
  const res = await apiFormData.post("/yardInspection/evidence", form);
  return res.data.data;
};

export interface UpdateYardInspectionEvidenceParams {
  inspectionId: string;
  evidenceId: string;
  title?: string;
  description?: string;
}

export const updateYardInspectionEvidence = async (
  params: UpdateYardInspectionEvidenceParams,
): Promise<YardInspectionEvidence> => {
  const res = await api.put("/yardInspection/evidence", {
    inspection_id: params.inspectionId,
    evidence_id: params.evidenceId,
    title: params.title,
    description: params.description,
  });
  return res.data.data;
};

export const deleteYardInspectionEvidence = async (
  inspectionId: string,
  evidenceId: string,
): Promise<void> => {
  await api.delete("/yardInspection/evidence", {
    params: { inspection_id: inspectionId, evidence_id: evidenceId },
  });
};

export interface GeofenceEventNotification {
  title?: string | null;
  description?: string | null;
  icon?: string | null;
  coordinates?: { lat: number; lon: number } | null;
}

/** One leg (ingreso o egreso) of a geocerca crossing correlated to an access
 *  event. `at` is the crossing time in epoch millis. */
export interface GeofenceCrossingLeg {
  event_id: string;
  entered_at?: number | null;
  exited_at?: number | null;
  coordinates: { lat: number; lon: number } | null;
  /**
   * Id del documento de `notifications` que disparó ESTE cruce ("Entrada a
   * Geocerca" en el ingreso, "Salida de Geocerca" en el egreso). Opcional y
   * anulable a propósito: los cruces anteriores a que el backend guardara el
   * vínculo no lo traen, y un backend sin desplegar no manda el campo. Sin id
   * el modal no dibuja enlace (ver `journeyNotificationId`).
   */
  notification_id?: string | null;
}

export interface InspectionGeofenceEvent {
  geofence_event_id: string | null;
  geofence_id: string | null;
  geofence_name: string | null;
  status: string | null;
  transition: string | null;
  entered_at: number | null;
  exited_at: number | null;
  duration: number | null;
  enter_coordinates: { lat: number; lon: number } | null;
  exit_coordinates: { lat: number; lon: number } | null;
  notifications: GeofenceEventNotification[];
  // Structured legs: the INGRESO is fixed when the inspection is created (the
  // entry already happened) and the EGRESO is resolved when the report modal
  // opens (the closest exit to the exit access event). Each is null until the
  // corresponding geocerca crossing exists.
  ingreso: GeofenceCrossingLeg | null;
  egreso: GeofenceCrossingLeg | null;
}

export interface InspectionGeofenceEventResult {
  data: InspectionGeofenceEvent | null;
  matchedBy?: string;
  reason?: string;
}

export const fetchInspectionGeofenceEvent = async (
  inspectionId: string,
): Promise<InspectionGeofenceEventResult> => {
  const res = await api.get("/yardInspection/geofenceEvent", {
    params: { inspection_id: inspectionId },
  });
  return {
    data: res.data?.data ?? null,
    matchedBy: res.data?.matched_by,
    reason: res.data?.reason,
  };
};
